import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ActorCard from '../components/ActorCard';
import SkeletonCard from '../components/ui/SkeletonCard';
import { getRandomActor } from '../api/api';

const ErrorCard = ({ onRetry }) => (
  <div className="bg-white border-[3px] border-[#1a1a1a] rounded-xl p-3 flex items-center gap-3 shadow-[3px_3px_0_#1a1a1a]">
    <div className="w-10 h-10 rounded-full bg-[#f0e6d3] border-2 border-[#1a1a1a] flex-shrink-0 flex items-center justify-center text-lg">
      ⚠️
    </div>
    <div className="flex-1">
      <p className="text-sm font-bold text-[#1a1a1a]">Couldn't load actor</p>
      <p className="text-xs text-[#888]">Tap retry to try again</p>
    </div>
    <button
      onClick={onRetry}
      className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded border-2 border-[#ff6b35] text-[#ff6b35] flex-shrink-0"
    >
      Retry
    </button>
  </div>
);

const Random = () => {
  const navigate = useNavigate();
  const [startActor, setStartActor] = useState(null);
  const [endActor, setEndActor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [difficulty, setDifficulty] = useState('medium');

  const loadActors = useCallback(async (signal) => {
    setLoading(true);
    setStartActor(null);
    setEndActor(null);
    let a1, a2;
    do {
      [a1, a2] = await Promise.all([
        getRandomActor(difficulty),
        getRandomActor(difficulty),
      ]);
      if (signal?.aborted) return;
    } while (a1 && a2 && a1.id === a2.id);
    setStartActor(a1);
    setEndActor(a2);
    setLoading(false);
  }, [difficulty]);

  useEffect(() => {
    const controller = new AbortController();
    loadActors(controller.signal);
    return () => controller.abort();
  }, [loadActors]);

  const ready = startActor && endActor;

  return (
    <div className="min-h-screen bg-[#fdf6ec] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">

        <div className="text-center mb-6">
          <span className="bg-[#ff6b35] text-white text-xs font-black tracking-[3px] uppercase px-3 py-1 rounded">
            FILMQUEST
          </span>
          <h1 className="text-2xl font-black text-[#1a1a1a] mt-3 mb-1">Random Game</h1>
          <p className="text-sm text-[#888]">Hope you got lucky!</p>
        </div>

        <div className="flex gap-2 mb-5">
          {['easy', 'medium', 'hard'].map(d => (
            <button
              key={d}
              onClick={() => setDifficulty(d)}
              disabled={loading}
              className={`flex-1 py-2 text-xs font-black uppercase tracking-wider rounded-lg border-[3px] border-[#1a1a1a] transition-all disabled:opacity-40 ${
                difficulty === d
                  ? 'bg-[#1a1a1a] text-white shadow-none'
                  : 'bg-white text-[#1a1a1a] shadow-[3px_3px_0_#1a1a1a]'
              }`}
            >
              {d}
            </button>
          ))}
        </div>

        <div className="flex flex-col gap-3 mb-6">
          <div>
            <p className="text-xs font-black text-[#ff6b35] uppercase tracking-widest mb-1.5">Start</p>
            {loading ? <SkeletonCard /> : !startActor ? <ErrorCard onRetry={loadActors} /> : (
              <ActorCard
                actorId={startActor.id}
                actorName={startActor.name}
                actorRole={startActor.known_for_department}
                actorImgUrl={startActor.profile_path}
                popularity={startActor.popularity}
              />
            )}
          </div>
          <div>
            <p className="text-xs font-black text-[#888] uppercase tracking-widest mb-1.5">Goal</p>
            {loading ? <SkeletonCard /> : !endActor ? <ErrorCard onRetry={loadActors} /> : (
              <ActorCard
                actorId={endActor.id}
                actorName={endActor.name}
                actorRole={endActor.known_for_department}
                actorImgUrl={endActor.profile_path}
                popularity={endActor.popularity}
              />
            )}
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <button
            onClick={() => navigate('/ingame', { state: { startActor, endActor } })}
            disabled={!ready}
            className="bg-[#ff6b35] text-white font-black text-base py-3 px-6 rounded-lg border-[3px] border-[#1a1a1a] shadow-[4px_4px_0_#1a1a1a] hover:shadow-[2px_2px_0_#1a1a1a] transition-shadow disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
          >
            🎬 Start Game
          </button>
          <button
            onClick={loadActors}
            disabled={loading}
            className="bg-white text-[#1a1a1a] font-black text-base py-3 px-6 rounded-lg border-[3px] border-[#1a1a1a] shadow-[4px_4px_0_#1a1a1a] hover:shadow-[2px_2px_0_#1a1a1a] transition-shadow disabled:opacity-40"
          >
            🔄 Retry
          </button>
          <button
            onClick={() => navigate('/')}
            className="text-[#ff6b35] text-sm font-bold underline text-center"
          >
            ← Main Menu
          </button>
        </div>

      </div>
    </div>
  );
};

export default Random;
